import { SuretyAccountStatus, SuretyAccountUnderwritingStatus } from "@inrev/common";
import { SuretyBond } from "@portal/api/bond/agent";
import { BondRequest } from "@portal/api/bond-request/agent";
import { ReactElement } from "react";
import { cn } from "../lib/utils";
import { Icon, IconType } from "../primitives";

type BondRequestStatusType = BondRequest["status"];
type SuretyAccountStatusType = SuretyAccountStatus;
export type BondStatusTagType = SuretyBond["status"];
export type StatusTagType =
	| BondRequestStatusType
	| SuretyAccountStatusType
	| typeof SuretyAccountUnderwritingStatus.processing;

type StatusTagProps = {
	type: StatusTagType | BondStatusTagType;
	label: string;
	isBond?: boolean;
	iconOnly?: boolean;
	condensed?: boolean;
} & (
	| {
			isBond: true;
			type: BondStatusTagType;
	  }
	| {
			isBond?: false;
			type: StatusTagType;
	  }
);

const textColorMap = {
	pending: "text-[#111835]",
	review: "text-intent-warning-text",
	approved: "text-intent-success-text",
	accepted: "text-teal-700",
	closed: "text-intent-neutral-text",
	declined: "text-intent-danger-text",
	active: "text-intent-info-text",
	processing: "text-secondary",
	bound: undefined,
};

const bondTextColorMap = {
	open: "text-intent-info-text",
	closed: "text-intent-neutral-text",
};

const bgColorMap = {
	pending: "bg-white",
	review: "bg-intent-warning-bg",
	approved: "bg-intent-success-bg",
	accepted: "bg-white",
	closed: "bg-white",
	declined: "bg-white",
	active: "bg-white",
	processing: "bg-surface-base",
	bound: undefined,
};

const bondBgColorMap = {
	open: "bg-transparent",
	closed: "bg-transparent",
};

const borderMap = {
	pending: "border border-[#111835]",
	review: "border border-intent-warning-text",
	approved: "border border-intent-success-text",
	accepted: "border border-teal-700",
	closed: "border border-intent-neutral-text",
	declined: "border border-intent-danger-text",
	active: "border border-intent-info-text",
	processing: "border border-bc-stronger",
	bound: undefined,
};

const bondBorderMap = {
	open: "",
	closed: "",
};

const iconMap: Record<StatusTagType, IconType | undefined> = {
	pending: "clock",
	review: "exclamation-triangle",
	approved: "check",
	accepted: "check-circle",
	closed: "minus-circle",
	declined: "x-circle",
	active: "bolt",
	processing: "hourglass",
	bound: undefined,
};

const bondIconMap: Record<BondStatusTagType, IconType | undefined> = {
	open: "shield-check",
	closed: "minus-circle",
};

export const StatusTag = ({ type, label, isBond, iconOnly, condensed }: StatusTagProps) => {
	const iconType = isBond ? bondIconMap[type] : iconMap[type];
	const iconColor = isBond ? bondTextColorMap[type] : textColorMap[type];

	return (
		<div
			title={condensed ? label : undefined}
			className={cn(
				"flex items-center gap-[6px] w-fit h-fit min-h-fit text-[13px] font-medium leading-[21px] select-none px-2 py-1 rounded-md",
				isBond ? bondTextColorMap[type] : textColorMap[type],
				isBond ? bondBgColorMap[type] : bgColorMap[type],
				isBond ? bondBorderMap[type] : borderMap[type],
				condensed && "py-1 px-2",
			)}
		>
			{iconType && <Icon type={iconType} size="mini" className={iconColor} />}
			{!iconOnly && <span>{label}</span>}
		</div>
	);
};

export const PendingStatusTag = (options: StatusTagOptions) => (
	<StatusTag type="pending" label="Pending" {...options} />
);

export const ReviewStatusTag = (options: StatusTagOptions) => (
	<StatusTag type="review" label="Underwriter Review" {...options} />
);

export const ApprovedStatusTag = (options: StatusTagOptions) => (
	<StatusTag type="approved" label="Approved" {...options} />
);

export const AcceptedStatusTag = (options: StatusTagOptions) => (
	<StatusTag type="accepted" label="Terms Accepted" {...options} />
);

export const ClosedStatusTag = (options: StatusTagOptions) => (
	<StatusTag type="closed" label="Closed" {...options} />
);

export const DeclinedStatusTag = (options: StatusTagOptions) => (
	<StatusTag type="declined" label="Declined" {...options} />
);

export const ActiveStatusTag = (options: StatusTagOptions) => (
	<StatusTag type="active" label="Active" {...options} />
);

export const BondOpenStatusTag = (options: StatusTagOptions) => (
	<StatusTag type="open" label="Open" isBond {...options} />
);
export const BondClosedStatusTag = (options: StatusTagOptions) => (
	<StatusTag type="closed" label="Closed" isBond {...options} />
);

export const ProcessingStatusTag = (options: StatusTagOptions) => (
	<StatusTag type="processing" label="Processing" {...options} />
);

const statusMap: Partial<
	Record<StatusTagType, (options: StatusTagOptions) => ReactElement<StatusTagProps>>
> = {
	pending: (options: StatusTagOptions) => <PendingStatusTag {...options} />,
	review: (options: StatusTagOptions) => <ReviewStatusTag {...options} />,
	approved: (options: StatusTagOptions) => <ApprovedStatusTag {...options} />,
	accepted: (options: StatusTagOptions) => <AcceptedStatusTag {...options} />,
	closed: (options: StatusTagOptions) => <ClosedStatusTag {...options} />,
	declined: (options: StatusTagOptions) => <DeclinedStatusTag {...options} />,
	active: (options: StatusTagOptions) => <ActiveStatusTag {...options} />,
	processing: (options: StatusTagOptions) => <ProcessingStatusTag {...options} />,
};

const bondStatusMap: Partial<
	Record<BondStatusTagType, (options: StatusTagOptions) => ReactElement<StatusTagProps>>
> = {
	open: (options: StatusTagOptions) => <BondOpenStatusTag {...options} />,
	closed: (options: StatusTagOptions) => <BondClosedStatusTag {...options} />,
};

type StatusTagOptions = {
	iconOnly?: boolean;
	condensed?: boolean;
};

export const statusDisplayLabelMap: Record<StatusTagType, string> = {
	pending: "Pending",
	review: "Underwriter Review",
	approved: "Approved",
	accepted: "Terms Accepted",
	closed: "Closed",
	declined: "Declined",
	active: "Active",
	processing: "Processing",
	bound: "Bound",
};

export const bondStatusDisplayLabelMap: Record<BondStatusTagType, string> = {
	open: "Open",
	closed: "Closed",
};

export const getStatusTagByStatus = (status: StatusTagType, options?: StatusTagOptions) => {
	const statusTag = statusMap[status];

	// 'bound' status is currently not supported
	if (statusTag === undefined) {
		return null;
	}

	return statusTag(options ?? {});
};

export const getStatusTagByBondStatus = (status: BondStatusTagType, options?: StatusTagOptions) => {
	const statusTag = bondStatusMap[status];

	if (statusTag === undefined) {
		return null;
	}

	return statusTag(options ?? {});
};
